import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { sqlReadRaw, sqlRead, sqlEscape } from "../../utils/sqlClient";
import { fmtDate, fmtCurrency } from "../../utils/format";
import { attachFlags, groupQuotes, buildCoverage, snapshotSummaries, quoteFieldPatch, attachQuotePayload, detachQuotePayload } from "../../utils/quoteLog";
import { QUOTE_STATUSES, REVIEW_STATUSES, DEFAULT_REVIEW_STATUS } from "../../config/quoteStatus";
import { LINE_KINDS, DEFAULT_LINE_KIND, MATCH_STATUSES, DEFAULT_MATCH_STATUS } from "../../config/quoteLines";
import { lineNormalizationPayload, mapLinePayload } from "../../utils/quoteLeveling";
import { useMsal } from "@azure/msal-react";
import { ItemAssemblyPicker } from "../../components/ItemAssemblyPicker";
import QuoteLevelingTable from "./QuoteLevelingTable";

// Quote Log (P3-A/P3-B, docs/implementation/P3_AUDIT_2026-09-12.md) — this
// project's vendor/sub quotes (sp_GetProjectQuotes, db/71: Quotes.ProjectID,
// falling back to the deal link for older rows), with the gap-report / expiry
// flags, the bid-log state an estimator sets here (quote status, review
// status, version / supersedes, RFQ requested and due dates — sp_UpdateQuote
// writes only the keys sent), "Attach an existing quote" for quotes saved
// without a deal id, and the bid-coverage view: the bid package's in-scope
// spec sections (cost codes) against live quotes whose category anchors to
// the same cost code. Quote content itself is edited on Quote Search and
// added through the Estimating Assistant.
// P3-D: each quote LINE can be normalized here beside its raw extraction —
// role in the offer (base / alternate / allowance / exclusion / freight / tax /
// other), estimator-confirmed catalog identity (Item / Assembly), a like-for-
// like quantity / unit reading and a note (sp_SaveQuoteLineNormalization,
// db/73; the raw description / qty / prices are never altered) — and every
// category with two or more live quotes shows the leveling matrix
// (QuoteLevelingTable.jsx) built from those normalized lines.
// P3-E: estimator LEVELING ADJUSTMENTS (QuoteLevelingAdjustments, Table4 of
// sp_GetProjectQuotes; sp_SaveQuoteLevelingAdjustment, db/74) sit beside a
// quote and raise / lower its leveled number for comparison only, and "Carry"
// writes the leveled winner of an entity into the ACTIVE (unlocked) revision
// as a P3-C quote-sourced estimate line (sp_SaveEstimateCostLineItem).

const BLOB_SAS = import.meta.env.VITE_QUOTE_DOCS_SAS || "";
const th = { fontSize: 10.5, fontWeight: 700, color: "var(--text3)", textAlign: "left", padding: "6px 8px", textTransform: "uppercase", letterSpacing: "0.03em", borderBottom: "1px solid var(--cs-border)", whiteSpace: "nowrap" };
const td = { fontSize: 12, padding: "5px 8px", borderBottom: "1px solid var(--cs-border)", verticalAlign: "top" };
const num = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
const inp = { fontSize: 11.5, border: "1px solid var(--cs-border)", borderRadius: 5, padding: "2px 5px", background: "var(--surface)", color: "var(--text)", boxSizing: "border-box" };
const btn = { fontSize: 11.5, fontWeight: 700, padding: "3px 9px", border: "1px solid var(--cs-border)", background: "var(--surface)", color: "var(--text2)", borderRadius: 6, cursor: "pointer", whiteSpace: "nowrap" };
const STATUS = {
  covered:         { label: "Covered",         bg: "#dcfce7", color: "#166534" },
  unreviewed_only: { label: "Not reviewed",    bg: "#e0f2fe", color: "#075985" },
  expired_only:    { label: "Nothing live",    bg: "#fef3c7", color: "#92400e" },
  uncovered:       { label: "No quote",        bg: "#fee2e2", color: "#991b1b" },
  no_cost_code:    { label: "No cost code",    bg: "#f3f4f6", color: "#374151" },
};
const SEVERITY = { high: "#b91c1c", medium: "#b45309", low: "var(--text3)" };
const pill = (s) => <span style={{ fontSize: 10.5, fontWeight: 700, padding: "1px 8px", borderRadius: 100, background: s.bg, color: s.color, whiteSpace: "nowrap" }}>{s.label}</span>;

export default function QuoteLog({ project, bidPkg }) {
  const { accounts } = useMsal();
  const userName = accounts?.[0]?.name || accounts?.[0]?.username || null;
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [lines, setLines] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [specSections, setSpecSections] = useState([]);
  const [unlinked, setUnlinked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const [attachPick, setAttachPick] = useState("");
  const [catalog, setCatalog] = useState({ items: [], assemblies: [], categories: [] }); // the Item / Assembly picker for line identity (P3-D)
  const [adjustments, setAdjustments] = useState([]);       // P3-E QuoteLevelingAdjustments of this project's quotes
  const [activeRevision, setActiveRevision] = useState(null); // the unlocked revision a leveled winner is carried into
  const [estimateLines, setEstimateLines] = useState([]);   // that revision's EstimateCostLineItems (which quote lines are already carried)

  const load = useCallback(() => {
    if (!project?.ProjectID) return;
    setLoading(true);
    Promise.all([
      sqlReadRaw(`EXEC sp_GetProjectQuotes @project_id=${+project.ProjectID}`),
      bidPkg?.BidPackageID ? sqlRead(`EXEC sp_GetSpecSections @bid_package_id=${+bidPkg.BidPackageID}`) : Promise.resolve([]),
      sqlRead("EXEC sp_GetUnlinkedQuotes").catch(() => []),
      sqlReadRaw("EXEC sp_GetEstimateCostLibrary").catch(() => null),
      sqlReadRaw(`EXEC sp_GetEstimateRevisions @project_id=${+project.ProjectID}`).catch(() => null),
    ]).then(async ([r, s, u, lib, revs]) => {
      setQuotes(r?.Table1 || []); setLines(r?.Table2 || []); setSnapshots(r?.Table3 || []); setAdjustments(r?.Table4 || []); setSpecSections(s || []); setUnlinked(u || []);
      if (lib) setCatalog({ categories: lib.Table1 || [], assemblies: lib.Table2 || [], items: lib.Table3 || [] });
      const revList = revs?.Table1 || [];
      const active = revList.find(x => !x.IsLocked) || null;
      setActiveRevision(active);
      if (active) {
        try { const li = await sqlRead(`EXEC sp_GetEstimateCostLineItems @revision_id=${+active.RevisionID}`); setEstimateLines(li || []); }
        catch (e) { console.error("Estimate lines load failed (carried markers hidden):", e); setEstimateLines([]); }
      } else setEstimateLines([]);
    }).catch(e => console.error("Quote log load failed:", e))
      .finally(() => setLoading(false));
  }, [project?.ProjectID, bidPkg?.BidPackageID]);
  useEffect(() => { load(); }, [load]);

  const flagged = useMemo(() => attachFlags(quotes), [quotes]);
  const groups = useMemo(() => groupQuotes(flagged), [flagged]);
  const coverage = useMemo(() => buildCoverage(specSections, flagged), [specSections, flagged]);
  const flagCount = flagged.reduce((n, q) => n + q.flags.length, 0);

  async function update(payload, label) {
    setSaving(true);
    try {
      const rows = await sqlRead(`EXEC sp_UpdateQuote @payload='${sqlEscape(JSON.stringify(payload))}'`);
      if (rows?.[0]?.Success === 0 || rows?.[0]?.Success === false) throw new Error(rows[0].ErrorMessage || "Save failed");
      load();
    } catch (e) { alert(`${label} failed: ${e.message}`); }
    finally { setSaving(false); }
  }
  const patchQuote = (quoteId, patch) => update(quoteFieldPatch(quoteId, patch), "Update");
  // P3-E — a leveling adjustment (upsert / delete) or carrying a leveled winner; both re-read the log.
  async function runProc(proc, payload, label) {
    setSaving(true);
    try {
      const rows = await sqlRead(`EXEC ${proc} @payload='${sqlEscape(JSON.stringify(payload))}'`);
      if (rows?.[0]?.Success === 0 || rows?.[0]?.Success === false) throw new Error(rows[0].ErrorMessage || "Save failed");
      load();
    } catch (e) { alert(`${label} failed: ${e.message}`); }
    finally { setSaving(false); }
  }
  const saveAdjustment = (payload) => runProc("sp_SaveQuoteLevelingAdjustment", { ...payload, created_by: payload.created_by || userName }, "Leveling adjustment");
  const carryLine = (payload) => runProc("sp_SaveEstimateCostLineItem", payload, "Carry into estimate");
  // P3-D — one line's normalization; re-reads the log so the leveling matrix follows.
  async function saveLine(payload) {
    setSaving(true);
    try {
      const rows = await sqlRead(`EXEC sp_SaveQuoteLineNormalization @payload='${sqlEscape(JSON.stringify(payload))}'`);
      if (rows?.[0]?.Success === 0 || rows?.[0]?.Success === false) throw new Error(rows[0].ErrorMessage || "Save failed");
      load();
    } catch (e) { alert(`Line update failed: ${e.message}`); }
    finally { setSaving(false); }
  }
  const attach = () => attachPick && update(attachQuotePayload(+attachPick, +project.ProjectID), "Attach").then(() => setAttachPick(""));
  const detach = (q) => { if (window.confirm(`Detach "${q.VendorName || "quote"} — ${q.Category || ""}" from this project? The quote is kept; it just stops appearing here.`)) update(detachQuotePayload(q.QuoteID), "Detach"); };

  if (loading) return <div style={{ padding: 24, color: "var(--text3)", fontSize: 13 }}>Loading quotes…</div>;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, marginRight: "auto" }}>💬 Quote Log</h2>
        <button onClick={() => navigate("/?panel=search")} style={btn}>Open Quote Search</button>
      </div>
      <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 10 }}>
        {flagged.length} quote{flagged.length === 1 ? "" : "s"} on this project{project?.HubSpotDealID ? ` (attached directly, or saved under HubSpot deal ${project.HubSpotDealID})` : ""}
        {flagCount ? <span style={{ color: "#b45309" }}> · {flagCount} flag{flagCount === 1 ? "" : "s"}</span> : null}.
        Quotes are added through the Estimating Assistant and corrected on Quote Search; here you set what each quote <em>is</em> (budgetary / formal / revised / withdrawn),
        whether it has been <em>reviewed</em>, its version and RFQ dates, and attach quotes saved without a deal. Coverage matches the bid package's in-scope spec sections to
        <strong> live</strong> quotes by cost code — expired, excluded, withdrawn and superseded quotes stay on the log but do not count.
      </div>

      {/* Attach an existing quote */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 14, padding: "8px 10px", border: "1px dashed var(--cs-border)", borderRadius: 8 }}>
        <span style={{ fontSize: 11.5, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Attach an existing quote</span>
        <select aria-label="Unlinked quote" value={attachPick} onChange={e => setAttachPick(e.target.value)} style={{ ...inp, width: 420 }} disabled={!unlinked.length || saving}>
          <option value="">{unlinked.length ? `— ${unlinked.length} quote${unlinked.length === 1 ? "" : "s"} not attached to any project —` : "— every saved quote is already attached to a project —"}</option>
          {unlinked.map(u => <option key={u.QuoteID} value={u.QuoteID}>{[u.VendorName || "no vendor", u.Category, u.Subcategory, fmtCurrency(u.TotalAmount), fmtDate(u.QuoteDate), u.hubspot_deal_name ? `deal: ${u.hubspot_deal_name}` : null].filter(Boolean).join(" · ")}</option>)}
        </select>
        <button onClick={attach} disabled={!attachPick || saving} style={btn} title="Sets the quote's ProjectID to this project. Nothing else on the quote changes.">Attach to this project</button>
      </div>

      {/* Bid coverage */}
      <section style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 4 }}>Bid coverage — spec sections vs. live quotes</div>
        {!specSections.length ? (
          <div style={{ fontSize: 12, color: "var(--text3)" }}>No spec sections on this project's bid package yet — add them on the Bid Package tab to see which scope has quotes.</div>
        ) : (
          <>
            <div style={{ fontSize: 11.5, color: "var(--text3)", marginBottom: 6 }}>
              {coverage.summary.sections} in-scope section{coverage.summary.sections === 1 ? "" : "s"}: {coverage.summary.covered} covered · {coverage.summary.unreviewedOnly} awaiting review · {coverage.summary.expiredOnly} with nothing live · {coverage.summary.uncovered} without a quote · {coverage.summary.noCostCode} without a cost code
            </div>
            <div style={{ overflowX: "auto", border: "1px solid var(--cs-border)", borderRadius: 8 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr><th style={th}>CSI</th><th style={th}>Spec section</th><th style={th}>Status</th><th style={th}>Quotes</th></tr></thead>
                <tbody>
                  {coverage.sections.map(s => (
                    <tr key={s.specSectionId} data-testid={`coverage-${s.status}`}>
                      <td style={{ ...td, fontFamily: "monospace", whiteSpace: "nowrap" }}>{s.csiCode || "—"}</td>
                      <td style={td}>{s.title}</td>
                      <td style={td}>{pill(STATUS[s.status])}</td>
                      <td style={td}>{s.quotes.length ? s.quotes.map(q => `${q.VendorName || "—"} ${fmtCurrency(q.TotalAmount) || ""}${q.flags.filter(f => ["expired", "excluded", "withdrawn", "superseded"].includes(f.code)).map(f => ` (${f.code})`).join("")}`).join(" · ") : <span style={{ color: "var(--text3)" }}>—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {coverage.unmatchedQuotes.length > 0 && (
              <div style={{ fontSize: 11.5, color: "var(--text3)", marginTop: 6 }}>
                {coverage.unmatchedQuotes.length} quote{coverage.unmatchedQuotes.length === 1 ? "" : "s"} outside the in-scope spec sections: {coverage.unmatchedQuotes.map(q => `${q.VendorName || "—"} (${q.Category || "no category"})`).join(", ")}.
              </div>
            )}
          </>
        )}
      </section>

      {/* Quotes by category */}
      <section>
        <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 4 }}>Quotes received</div>
        {!flagged.length ? (
          <div style={{ fontSize: 12, color: "var(--text3)" }}>No quotes on this project yet{project?.HubSpotDealID ? "" : " — this project has no HubSpot deal id, so only quotes attached above appear here"}.</div>
        ) : groups.map(g => (
          <div key={g.category} style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text2)", margin: "6px 0 4px" }}>{g.category} <span style={{ fontWeight: 500, color: "var(--text3)" }}>· {g.quotes.length}</span></div>
            <div style={{ overflowX: "auto", border: "1px solid var(--cs-border)", borderRadius: 8 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1180 }}>
                <thead><tr><th style={{ ...th, width: 24 }}></th><th style={th}>Vendor</th><th style={th}>Subcategory</th><th style={th}>Quote</th><th style={th}>Review</th><th style={th}>Version</th><th style={th}>Requested</th><th style={th}>Due</th><th style={th}>Quote date</th><th style={th}>Valid until</th><th style={{ ...th, textAlign: "right" }}>Total</th><th style={{ ...th, textAlign: "right" }}>Lines</th><th style={th}>Cost / X</th><th style={th}>Flags</th><th style={th}>Source</th><th style={th}></th></tr></thead>
                <tbody>
                  {g.quotes.map(q => (
                    <QuoteRows key={q.QuoteID} q={q} qLines={lines.filter(l => l.QuoteID === q.QuoteID)} snaps={snapshotSummaries(q.QuoteID, snapshots)} all={flagged}
                      open={expanded.has(q.QuoteID)} saving={saving} onPatch={patch => patchQuote(q.QuoteID, patch)} onDetach={() => detach(q)}
                      catalog={catalog} onLine={saveLine}
                      onToggle={() => setExpanded(s => { const n = new Set(s); n.has(q.QuoteID) ? n.delete(q.QuoteID) : n.add(q.QuoteID); return n; })} />
                  ))}
                </tbody>
              </table>
            </div>
            {g.quotes.length >= 2 && (
              <QuoteLevelingTable quotes={g.quotes} lines={lines.filter(l => g.quotes.some(q => q.QuoteID === l.QuoteID))}
                adjustments={adjustments.filter(a => g.quotes.some(q => q.QuoteID === a.QuoteID))} carriedLines={estimateLines} catalog={catalog}
                revisionId={activeRevision?.RevisionID ?? null} saving={saving} onSaveAdjustment={saveAdjustment} onCarry={carryLine} />
            )}
          </div>
        ))}
      </section>
    </div>
  );
}

export function QuoteRows({ q, qLines, snaps, all = [], open, saving = false, onToggle, onPatch, onDetach, catalog = null, onLine = null }) {
  const canLine = typeof onLine === "function";
  const review = q.ReviewStatus || DEFAULT_REVIEW_STATUS;
  const dateInput = (col) => (
    <input aria-label={col} type="date" style={{ ...inp, width: 118 }} defaultValue={q[col] ? String(q[col]).slice(0, 10) : ""} key={`${col}-${q[col] ?? ""}`} disabled={saving}
      onBlur={e => { const v = e.target.value || null; if (v !== (q[col] ? String(q[col]).slice(0, 10) : null)) onPatch({ [col]: v }); }} />
  );
  return (
    <>
      <tr data-testid={`quote-row-${q.QuoteID}`}>
        <td style={td}><button onClick={onToggle} aria-label={open ? "Collapse" : "Expand"} style={{ ...btn, padding: "0 6px", transform: open ? "rotate(90deg)" : "none" }}>▸</button></td>
        <td style={{ ...td, fontWeight: 700 }}>{q.VendorName || <span style={{ color: "#b91c1c" }}>no vendor</span>}{q.ManufacturerName ? <div style={{ fontSize: 10.5, fontWeight: 400, color: "var(--text3)" }}>{q.ManufacturerName}</div> : null}</td>
        <td style={td}>{q.Subcategory || "—"}</td>
        <td style={td}>
          <select aria-label="Quote status" style={inp} value={q.QuoteStatus || ""} disabled={saving} onChange={e => onPatch({ QuoteStatus: e.target.value || null })}>
            <option value="">— unset —</option>
            {QUOTE_STATUSES.map(s => <option key={s.key} value={s.key} title={s.description}>{s.label}</option>)}
          </select>
        </td>
        <td style={td}>
          <select aria-label="Review status" style={inp} value={review} disabled={saving} onChange={e => onPatch({ ReviewStatus: e.target.value })}>
            {REVIEW_STATUSES.map(s => <option key={s.key} value={s.key} title={s.description}>{s.label}</option>)}
          </select>
        </td>
        <td style={td}>
          <input aria-label="Quote version" style={{ ...inp, width: 64 }} key={`v-${q.QuoteVersion ?? ""}`} defaultValue={q.QuoteVersion || ""} placeholder="Rev" disabled={saving}
            onBlur={e => { const v = e.target.value.trim() || null; if (v !== (q.QuoteVersion || null)) onPatch({ QuoteVersion: v }); }} />
          <select aria-label="Supersedes" style={{ ...inp, width: 110, marginTop: 2 }} value={q.SupersedesQuoteID ?? ""} disabled={saving} title="The earlier quote this one replaces — the old one stays as history and drops out of coverage"
            onChange={e => onPatch({ SupersedesQuoteID: e.target.value ? +e.target.value : null })}>
            <option value="">supersedes: —</option>
            {all.filter(o => o.QuoteID !== q.QuoteID && (o.Category || "") === (q.Category || "")).map(o => <option key={o.QuoteID} value={o.QuoteID}>#{o.QuoteID} {o.VendorName || ""} {fmtDate(o.QuoteDate)}</option>)}
          </select>
        </td>
        <td style={td}>{dateInput("RequestedDate")}</td>
        <td style={td}>{dateInput("DueDate")}</td>
        <td style={td}>{fmtDate(q.QuoteDate)}</td>
        <td style={td}>{q.ValidUntil ? fmtDate(q.ValidUntil) : "—"}</td>
        <td style={num}>{fmtCurrency(q.TotalAmount) || "—"}</td>
        <td style={num}>{q.LineItemCount}</td>
        <td style={td}>{snaps.length ? snaps.map(s => s.text).join(" · ") : "—"}</td>
        <td style={td}>{q.flags.length ? q.flags.map(f => <div key={f.code} style={{ fontSize: 11, color: SEVERITY[f.severity] }} title={f.message}>⚠ {f.code.replace(/_/g, " ")}</div>) : <span style={{ color: "#166534" }}>✓</span>}</td>
        <td style={td}>{q.SourceFileUrl ? <a href={`${q.SourceFileUrl}${BLOB_SAS ? `?${BLOB_SAS}` : ""}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11.5 }}>{q.SourceFile || "file"}</a> : <span style={{ color: "var(--text3)" }}>—</span>}</td>
        <td style={td}>{q.ProjectID != null ? <button onClick={onDetach} disabled={saving} style={{ ...btn, color: "var(--text3)", padding: "1px 6px" }} title="Detach from this project (the quote is kept)">detach</button> : <span style={{ fontSize: 10.5, color: "var(--text3)" }} title="Appears through the HubSpot deal link only; attaching it directly is not needed">via deal</span>}</td>
      </tr>
      {open && (
        <tr>
          <td></td>
          <td colSpan={15} style={{ ...td, background: "var(--surface2)" }}>
            {(q.Scope || q.Exclusions) && (
              <div style={{ fontSize: 11.5, marginBottom: 6 }}>
                {q.Scope && <div><strong>Scope:</strong> {q.Scope}</div>}
                {q.Exclusions && <div style={{ color: "#92400e" }}><strong>Exclusions:</strong> {q.Exclusions}</div>}
              </div>
            )}
            {qLines.length ? (
              <table style={{ borderCollapse: "collapse", fontSize: 11.5, width: "100%" }}>
                <thead><tr><th style={th}>#</th><th style={th}>Description (as quoted)</th><th style={{ ...th, textAlign: "right" }}>Qty</th><th style={th}>Unit</th><th style={{ ...th, textAlign: "right" }}>Unit price</th><th style={{ ...th, textAlign: "right" }}>Extended</th><th style={th}>Kind</th><th style={th}>Maps to (catalog)</th><th style={th}>Match</th><th style={th}>Normalized qty / unit</th><th style={th}>Note</th></tr></thead>
                <tbody>{qLines.map(l => <QuoteLineRow key={l.LineItemID} l={l} saving={saving} canLine={canLine} catalog={catalog} onLine={onLine} />)}</tbody>
              </table>
            ) : <span style={{ fontSize: 11.5, color: "var(--text3)" }}>Lump sum — no line items were extracted. Add them on Quote Search ▸ Edit so this quote can be leveled.</span>}
            {q.flags.length > 0 && <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 11.5 }}>{q.flags.map(f => <li key={f.code} style={{ color: SEVERITY[f.severity] }}>{f.message}</li>)}</ul>}
            <div style={{ fontSize: 10.5, color: "var(--text3)", marginTop: 6 }}>Loaded {fmtDate(q.ReceivedDate)}{q.LoadedByName ? ` by ${q.LoadedByName}` : ""}{q.ExtractedByAI ? " · AI-extracted" : ""} · quote #{q.QuoteID}</div>
          </td>
        </tr>
      )}
    </>
  );
}


// P3-D — one quote line: the raw extraction (read-only, evidence) beside its
// normalization. Every edit writes exactly the changed key through
// sp_SaveQuoteLineNormalization; the quoted qty / unit / prices are never touched.
function QuoteLineRow({ l, saving, canLine, catalog, onLine }) {
  const status = l.MatchStatus || DEFAULT_MATCH_STATUS;
  const kind = l.LineKind || DEFAULT_LINE_KIND;
  const statusColor = { confirmed: "#166534", suggested: "#075985", rejected: "var(--text3)", unmatched: "#b45309" }[status];
  const value = { kind: l.ItemID != null ? "item" : l.AssemblyID != null ? "assembly" : null, id: l.ItemID ?? l.AssemblyID ?? null };
  const dim = kind === "exclusion" ? { opacity: 0.75 } : {};
  return (
    <tr data-testid={`quote-line-${l.LineItemID}`} style={dim}>
      <td style={td}>{l.LineNumber}</td>
      <td style={td}>{l.Description}</td>
      <td style={num}>{l.Quantity ?? "—"}</td>
      <td style={td}>{l.Unit || "—"}</td>
      <td style={num}>{fmtCurrency(l.UnitPrice) || "—"}</td>
      <td style={num}>{fmtCurrency(l.ExtendedPrice) || "—"}</td>
      <td style={td}>
        {canLine ? (
          <select aria-label={`Line kind ${l.LineItemID}`} style={inp} value={kind} disabled={saving} onChange={e => onLine(lineNormalizationPayload(l.LineItemID, { LineKind: e.target.value }))}>
            {LINE_KINDS.map(k => <option key={k.key} value={k.key} title={k.description}>{k.label}</option>)}
          </select>
        ) : kind}
      </td>
      <td style={{ ...td, minWidth: 220 }}>
        {canLine && catalog ? (
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <div style={{ flex: 1 }}>
              <ItemAssemblyPicker items={catalog.items} assemblies={catalog.assemblies} categories={catalog.categories} value={value}
                onSelect={e => onLine(mapLinePayload(l.LineItemID, { kind: e.kind, id: e.id }))} />
            </div>
            {value.id != null && <button aria-label={`Clear mapping ${l.LineItemID}`} onClick={() => onLine(mapLinePayload(l.LineItemID, null))} disabled={saving} style={{ ...btn, padding: "1px 6px" }} title="Clear the catalog identity (unmatched)">✕</button>}
          </div>
        ) : (l.MappedCode ? `${l.MappedCode} — ${l.MappedName || ""}` : <span style={{ color: "var(--text3)" }}>—</span>)}
      </td>
      <td style={td}>
        <span style={{ fontSize: 10.5, fontWeight: 700, color: statusColor }} title={MATCH_STATUSES.find(m => m.key === status)?.description}>{status}{l.MatchConfidence != null ? ` ${Math.round(+l.MatchConfidence * 100)} %` : ""}{l.MatchSource && l.MatchSource !== "estimator" ? ` (${l.MatchSource})` : ""}</span>
        {canLine && status === "suggested" && value.id != null && (
          <div style={{ display: "flex", gap: 4, marginTop: 2 }}>
            <button onClick={() => onLine(lineNormalizationPayload(l.LineItemID, { MatchStatus: "confirmed", MatchSource: "estimator" }))} disabled={saving} style={{ ...btn, padding: "1px 6px", color: "#166534" }}>Confirm</button>
            <button onClick={() => onLine(lineNormalizationPayload(l.LineItemID, { MatchStatus: "rejected" }))} disabled={saving} style={{ ...btn, padding: "1px 6px" }}>Reject</button>
          </div>
        )}
      </td>
      <td style={td}>
        {canLine ? (
          <div style={{ display: "flex", gap: 4 }}>
            <input aria-label={`Normalized quantity ${l.LineItemID}`} type="number" min={0} step="any" style={{ ...inp, width: 70 }} key={`nq-${l.NormalizedQuantity ?? ""}`} defaultValue={l.NormalizedQuantity ?? ""} placeholder={l.Quantity ?? "qty"} disabled={saving}
              onBlur={e => { const v = e.target.value === "" ? null : +e.target.value; if (v !== (l.NormalizedQuantity == null ? null : +l.NormalizedQuantity)) onLine(lineNormalizationPayload(l.LineItemID, { NormalizedQuantity: v })); }} />
            <input aria-label={`Normalized unit ${l.LineItemID}`} style={{ ...inp, width: 56 }} key={`nu-${l.NormalizedUnit ?? ""}`} defaultValue={l.NormalizedUnit || ""} placeholder={l.Unit || "unit"} disabled={saving}
              onBlur={e => { const v = e.target.value.trim() || null; if (v !== (l.NormalizedUnit || null)) onLine(lineNormalizationPayload(l.LineItemID, { NormalizedUnit: v })); }} />
          </div>
        ) : (l.NormalizedQuantity != null || l.NormalizedUnit ? `${l.NormalizedQuantity ?? ""} ${l.NormalizedUnit || ""}` : "—")}
      </td>
      <td style={td}>
        {canLine ? (
          <input aria-label={`Normalized note ${l.LineItemID}`} style={{ ...inp, width: 160 }} key={`nn-${l.NormalizedNote ?? ""}`} defaultValue={l.NormalizedNote || ""} placeholder="why / how normalized" disabled={saving}
            onBlur={e => { const v = e.target.value.trim() || null; if (v !== (l.NormalizedNote || null)) onLine(lineNormalizationPayload(l.LineItemID, { NormalizedNote: v })); }} />
        ) : (l.NormalizedNote || "—")}
      </td>
    </tr>
  );
}
